<template>
    <div class="min-h-screen bg-gray-200 flex items-center justify-center px-4 py-6">
        <div class="w-full max-w-xl text-center">
            <p class="text-sm text-gray-700 mb-4">
                Scan a member QR code, or type the short code shown under it.
            </p>
            <form @submit.prevent="submitToken">
                <label class="block text-sm font-medium text-gray-700 mb-1" for="qr-token">Scan or type QR code</label>
                <input
                    id="qr-token"
                    ref="tokenInput"
                    v-model="token"
                    type="text"
                    autocomplete="off"
                    class="w-full border border-gray-500 px-3 py-2 focus:outline-none text-center"
                    placeholder="Waiting for scan..."
                />
                <div class="mt-3 flex flex-wrap justify-center">
                    <button type="submit" class="border border-gray-400 bg-gray-200 hover:bg-white px-4 py-2 text-sm mr-2">
                        Submit
                    </button>
                    <button type="button" class="border border-gray-400 bg-gray-200 hover:bg-white px-4 py-2 text-sm" @click="toggleWebcam">
                        {{ webcamEnabled ? 'Stop Webcam' : 'Enable Webcam' }}
                    </button>
                </div>
            </form>
            <div v-show="webcamEnabled" id="qr-reader" class="mt-6 mx-auto max-w-md bg-black"></div>
            <div v-if="result" class="mt-6 p-4 border text-sm" :class="resultClasses">
                <div class="font-medium mb-1">{{ resultTitle }}</div>
                <div v-if="result.member_name">Member: {{ result.member_name }}</div>
                <div v-if="result.application_form_name">Application Form: {{ result.application_form_name }}</div>
                <div v-if="result.scanned_at">Scanned: {{ formatScannedAt(result.scanned_at) }}</div>
                <div v-if="result.message">{{ result.message }}</div>
            </div>
        </div>
        <notifications group="main" position="bottom right" />
    </div>
</template>

<script>
import axios from 'axios'
import { Html5Qrcode } from 'html5-qrcode'
import moment from 'moment'

export default {
    name: 'QrScan',
    data() {
        return {
            token: '',
            result: null,
            submitting: false,
            webcamEnabled: false,
            scanner: null
        }
    },
    computed: {
        resultTitle() {
            if (!this.result) {
                return ''
            }

            if (this.result.status === 'scanned') {
                return 'Scan recorded'
            }

            if (this.result.status === 'already_scanned') {
                return 'Already scanned'
            }

            return 'QR code not found'
        },
        resultClasses() {
            if (!this.result) {
                return ''
            }

            if (this.result.status === 'scanned') {
                return 'bg-green-100 border-green-400 text-green-800'
            }

            if (this.result.status === 'already_scanned') {
                return 'bg-yellow-100 border-yellow-400 text-yellow-800'
            }

            return 'bg-red-100 border-red-400 text-red-800'
        }
    },
    mounted() {
        this.focusInput()
        // Scanners type into whatever is focused, so capture keystrokes at the window
        window.addEventListener('keydown', this.onWindowKeydown)
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.onWindowKeydown)
        this.stopWebcam()
    },
    methods: {
        inputIsFocused() {
            return this.$refs.tokenInput && document.activeElement === this.$refs.tokenInput
        },
        onWindowKeydown(event) {
            if (this.inputIsFocused() || event.ctrlKey || event.metaKey || event.altKey) {
                return
            }

            if (event.key === 'Enter') {
                event.preventDefault()
                this.submitToken()
                return
            }

            if (event.key.length === 1) {
                event.preventDefault()
                this.token += event.key
            }
        },
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.tokenInput) {
                    this.$refs.tokenInput.focus()
                }
            })
        },
        formatScannedAt(value) {
            if (!value) {
                return ''
            }

            return moment(value).format('DD/MM/YYYY h:mm a')
        },
        submitToken() {
            const token = (this.token || '').trim()

            if (!token || this.submitting) {
                return
            }

            this.scanToken(token)
        },
        scanToken(token) {
            this.submitting = true

            axios.post(process.env.VUE_APP_API_URL + '/qr-codes/scan', { token })
                .then((response) => {
                    this.result = response.data
                })
                .catch((error) => {
                    this.result = error.response && error.response.data
                        ? error.response.data
                        : { status: 'not_found', message: 'QR code not found' }
                })
                .finally(() => {
                    this.token = ''
                    this.submitting = false
                    this.focusInput()
                })
        },
        async toggleWebcam() {
            if (this.webcamEnabled) {
                await this.stopWebcam()
                this.focusInput()
                return
            }

            this.scanner = new Html5Qrcode('qr-reader')

            try {
                await this.scanner.start(
                    { facingMode: 'environment' },
                    { fps: 10, qrbox: { width: 250, height: 250 } },
                    (decodedText) => {
                        const value = (decodedText || '').trim()

                        if (value && !this.submitting) {
                            this.scanToken(value)
                        }
                    }
                )
                this.webcamEnabled = true
            } catch (error) {
                this.scanner = null
                this.$notify({
                    group: 'main',
                    type: 'error',
                    title: 'Webcam',
                    text: 'Unable to start the webcam for QR scanning.',
                    duration: 5000
                })
            }
        },
        async stopWebcam() {
            if (!this.scanner) {
                this.webcamEnabled = false
                return
            }

            try {
                await this.scanner.stop()
                this.scanner.clear()
            } catch (error) {
                // Camera may already be stopped
            }

            this.scanner = null
            this.webcamEnabled = false
        }
    }
}
</script>
